import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import type {
  GetRecentActivitiesApiResponse,
  RecentActivityListItemApiResponse,
} from "@/types/api/recent-activities";

export interface CaseUnifiedActivity {
  id: string;
  type:
    | "email"
    | "call"
    | "sms"
    | "note"
    | "promise"
    | "payment"
    | "status_change"
    | "communication"
    | "case_note"
    | "system"
    | "case"
    | "dispute";
  tag: "Activity" | "Note" | "Communication";
  title: string;
  description: string;
  outcome?: string;
  createdBy: string;
  createdAt: Date;
}

const ACTIVITY_TYPE_TO_TAG: Record<string, CaseUnifiedActivity["tag"]> = {
  note: "Note",
  email: "Communication",
  phone: "Communication",
  sms: "Communication",
  communication: "Communication",
  promise: "Activity",
  status_change: "Activity",
  payment: "Activity",
  system: "Activity",
  case: "Activity",
  dispute: "Activity",
};

const ACTIVITY_TYPE_TO_TYPE: Record<string, CaseUnifiedActivity["type"]> = {
  email: "email",
  phone: "call",
  sms: "sms",
  communication: "communication",
  note: "note",
  promise: "promise",
  payment: "payment",
  status_change: "status_change",
  system: "system",
  case: "case_note",
  dispute: "dispute",
};

function mapApiToCaseUnifiedActivity(
  item: RecentActivityListItemApiResponse
): CaseUnifiedActivity {
  const tag = ACTIVITY_TYPE_TO_TAG[item.activityType] ?? "Activity";
  const type = ACTIVITY_TYPE_TO_TYPE[item.activityType] ?? "system";

  let outcome: string | undefined;
  if (item.metadataJson) {
    try {
      const metadata = JSON.parse(item.metadataJson) as Record<string, unknown>;
      if (typeof metadata.outcome === "string") {
        outcome = metadata.outcome;
      }
    } catch {
      // ignore malformed metadata
    }
  }

  return {
    id: String(item.id),
    type,
    tag,
    title: item.title,
    description: item.description,
    outcome,
    createdBy: item.actorName,
    createdAt: new Date(item.createdAt),
  };
}

function buildMockActivities(): CaseUnifiedActivity[] {
  const items: CaseUnifiedActivity[] = [
    {
      id: "mock-action-1",
      type: "status_change",
      tag: "Activity",
      title: "Case status changed to Active",
      description: "Case status changed to Active",
      createdBy: "Sarah Johnson",
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    },
    {
      id: "mock-action-2",
      type: "call",
      tag: "Communication",
      title: "Outbound call made to customer",
      description: "Attempted phone call - no answer",
      outcome: "Left voicemail requesting callback",
      createdBy: "Sarah Johnson",
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    },
    {
      id: "mock-action-3",
      type: "email",
      tag: "Communication",
      title: "Payment reminder email sent",
      description: "Sent payment reminder email",
      outcome: "Email delivered successfully",
      createdBy: "System",
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    },
    {
      id: "mock-note-1",
      type: "note",
      tag: "Note",
      title: "Note Added",
      description:
        "Customer requested payment plan options. Sent payment plan proposal.",
      createdBy: "Mike Chen",
      createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    },
    {
      id: "mock-note-2",
      type: "note",
      tag: "Note",
      title: "Note Added",
      description:
        "Spoke with accounts payable manager. Promised payment by end of week.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    },
    {
      id: "mock-note-3",
      type: "note",
      tag: "Note",
      title: "Note Added",
      description:
        "Case assigned to collections team. Customer has been unresponsive to previous payment reminders.",
      createdBy: "System",
      createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
    },
  ];
  return items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export function useCaseRecentActivities(caseId: string | undefined) {
  const { apiGet } = useApi();
  const mockActivities = buildMockActivities();

  return useModeAwareQuery<CaseUnifiedActivity[], Error>(
    ["case-recent-activities", caseId],
    async ({ signal }) => {
      if (!caseId) return [];
      try {
        const response = await apiGet<GetRecentActivitiesApiResponse>(
          `/api/v1/recent-activities/collection-cases/${caseId}`,
          { page: 1, pageSize: 50 },
          signal
        );
        return response.data
          .map(mapApiToCaseUnifiedActivity)
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      } catch (error) {
        console.error("Error fetching case recent activities:", error);
        return [];
      }
    },
    mockActivities,
    {
      enabled: !!caseId,
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}
